import { useCallback, useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Wallet, RefreshCw } from 'lucide-react';

const moeda = (valor) =>
  Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const mesAtual = () => {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Fortaleza',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());

  const ano = partes.find((p) => p.type === 'year')?.value;
  const mes = partes.find((p) => p.type === 'month')?.value;
  return `${ano}-${mes}`;
};

export default function RecebimentosSociosPage() {
  const { user } = useAuth();
  const gestor = user?.role === 'gestor';

  const [competencia, setCompetencia] = useState(mesAtual);
  const [simulacao, setSimulacao] = useState(null);
  const [fechamentos, setFechamentos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [processando, setProcessando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [resSimulacao, resHistorico] = await Promise.all([
        api.get('/gestor/recebimentos-socios/simulacao', {
          params: { competencia },
        }),
        api.get('/gestor/recebimentos-socios/fechamentos'),
      ]);
      setSimulacao(resSimulacao.data);
      setFechamentos(resHistorico.data?.fechamentos || []);
    } catch (erro) {
      toast.error(formatApiError(erro));
    } finally {
      setCarregando(false);
    }
  }, [competencia]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const fechamentoAtual = fechamentos.find(
    (item) => item.competencia === competencia
  );

  const fecharMes = async () => {
    if (!gestor || processando || fechamentoAtual) return;

    if (!window.confirm(
      `Confirmar o fechamento de ${competencia}? Essa ação registra os valores dos sócios.`
    )) return;

    setProcessando(true);
    try {
      await api.post(
        `/gestor/recebimentos-socios/fechamentos/${competencia}`
      );
      toast.success('Fechamento registrado com sucesso.');
      await carregar();
    } catch (erro) {
      toast.error(formatApiError(erro));
    } finally {
      setProcessando(false);
    }
  };

  const confirmarPagamento = async (fechamento, participacao) => {
    if (!gestor || processando || participacao.status !== 'pendente') return;

    if (!window.confirm(
      `Confirmar o pagamento de ${moeda(participacao.valor_devido)} para ${participacao.socio_nome}?`
    )) return;

    setProcessando(true);
    try {
      await api.post(
        `/gestor/recebimentos-socios/fechamentos/${fechamento.competencia}/participacoes/${participacao.id}/pagar`
      );
      toast.success('Pagamento confirmado.');
      await carregar();
    } catch (erro) {
      toast.error(formatApiError(erro));
    } finally {
      setProcessando(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white">
            Recebimentos dos Sócios
          </h1>
          <p className="text-sm text-zinc-400">
            Simulação, fechamento mensal e acompanhamento dos pagamentos.
          </p>
        </div>
        <Button variant="outline" onClick={carregar} disabled={carregando}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <Card className="bg-[#141414] border-zinc-800">
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                Competência
              </label>
              <Input
                type="month"
                value={competencia}
                onChange={(e) => setCompetencia(e.target.value)}
                className="bg-[#101010] border-zinc-700 text-white"
              />
            </div>
            {gestor && !fechamentoAtual && (
              <Button
                onClick={fecharMes}
                disabled={processando || carregando || !simulacao}
              >
                Registrar fechamento
              </Button>
            )}
          </div>

          {carregando ? (
            <p className="text-zinc-400">Carregando dados...</p>
          ) : simulacao ? (
            <>
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#007AFF]" />
                <span className="text-sm text-zinc-400">
                  Lucro distribuível estimado
                </span>
              </div>
              <p className="text-3xl font-bold text-white">
                {moeda(simulacao.lucro_distribuivel)}
              </p>
              <p className="text-xs text-zinc-500">
                Simulação informativa. O fechamento registra os valores do mês.
              </p>

              <div className="grid gap-3 md:grid-cols-2">
                {(simulacao.participacoes || []).map((item, indice) => (
                  <div
                    key={`${item.nome}-${indice}`}
                    className="rounded-lg border border-zinc-800 p-4"
                  >
                    <p className="font-semibold text-white">{item.nome}</p>
                    <p className="text-sm text-zinc-400">
                      Participação: {item.percentual}%
                    </p>
                    <p className="text-xl font-bold text-[#007AFF] mt-2">
                      {moeda(item.valor_previsto)}
                    </p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-zinc-400">
              Nenhuma simulação disponível.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white">
          Histórico de fechamentos
        </h2>

        {fechamentos.length === 0 && (
          <p className="text-zinc-400">
            Nenhum fechamento encontrado.
          </p>
        )}

        {fechamentos.map((fechamento, indice) => (
          <Card
            key={fechamento.id || `${fechamento.competencia}-${indice}`}
            className="bg-[#141414] border-zinc-800"
          >
            <CardContent className="p-5 space-y-4">
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {fechamento.competencia}
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Pagamento previsto: {
                      fechamento.data_pagamento_prevista || 'Não informado'
                    }
                  </p>
                </div>
                <Badge variant="outline">
                  {fechamento.status_fechamento || fechamento.status || 'Fechado'}
                </Badge>
              </div>

              {fechamento.ambiente === 'teste' && (
                <p className="text-amber-400 text-sm font-semibold">
                  Registro de teste — não representa pagamento real.
                </p>
              )}

              {(fechamento.participacoes || []).map((item, posicao) => (
                <div
                  key={item.id || posicao}
                  className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-800 pt-3"
                >
                  <div>
                    <p className="font-semibold text-white">
                      {item.socio_nome}
                    </p>
                    <p className="text-sm text-zinc-400">
                      {item.percentual}% — {moeda(item.valor_devido)}
                    </p>
                    <p className="text-xs text-zinc-500">
                      Situação: {item.status}
                    </p>
                  </div>

                  {gestor && item.status === 'pendente' &&
                    fechamento.ambiente !== 'teste' && (
                    <Button
                      size="sm"
                      disabled={processando}
                      onClick={() => confirmarPagamento(fechamento, item)}
                    >
                      Confirmar pagamento
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
